import { CapacitorHttp } from "@capacitor/core";
import type { AxiosAdapter } from "axios";

/**
 * Serialize axios `config.params` onto the URL. With a custom adapter, axios
 * does NOT append params for us (the built-in xhr/http adapters call buildURL
 * themselves), so without this the query string is silently dropped on native —
 * e.g. GET /api/journey-progress?journeyType=… loses its required param and the
 * API 400s, surfacing as a failed journey page on iOS/Android while web works.
 * Values are coerced to strings; null/undefined are skipped; arrays repeat the key.
 */
function appendParams(url: string, params: unknown): string {
	if (!params || typeof params !== "object") return url;
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(
		params as Record<string, unknown>,
	)) {
		if (value === undefined || value === null) continue;
		if (Array.isArray(value)) {
			for (const item of value) {
				if (item !== undefined && item !== null) {
					search.append(key, String(item));
				}
			}
		} else {
			search.append(key, String(value));
		}
	}
	const qs = search.toString();
	if (!qs) return url;
	return url.includes("?") ? `${url}&${qs}` : `${url}?${qs}`;
}

/**
 * Custom Axios adapter that uses Capacitor's native HTTP plugin
 * instead of XMLHttpRequest/fetch on native platforms (iOS/Android).
 *
 * This bypasses WKWebView networking restrictions and uses
 * native URLSession on iOS, which provides better reliability
 * and performance for API requests.
 *
 * Falls back to fetch for FormData (file uploads) since
 * CapacitorHttp doesn't handle multipart/form-data well.
 */
export const capacitorHttpAdapter: AxiosAdapter = async (config) => {
	try {
		// Detect FormData - use native fetch instead
		if (config.data instanceof FormData) {
			// Build full URL (including any axios query params)
			const url = appendParams(
				`${config.baseURL || ""}${config.url || ""}`,
				config.params,
			);

			// Prepare fetch options
			const fetchOptions: RequestInit = {
				method: config.method?.toUpperCase() || "POST",
				body: config.data,
				headers: {
					// Don't set Content-Type for FormData - browser will set it with boundary
					...Object.fromEntries(
						Object.entries(config.headers || {}).filter(
							([key]) => key.toLowerCase() !== "content-type",
						),
					),
				},
				signal: config.signal as AbortSignal | undefined,
			};

			// Use native fetch
			const response = await fetch(url, fetchOptions);

			// Parse response
			let data: unknown;
			const contentType = response.headers.get("content-type");
			if (contentType?.includes("application/json")) {
				data = await response.json();
			} else {
				data = await response.text();
			}

			// Check for HTTP errors
			if (!response.ok) {
				const error = new Error(
					`HTTP ${response.status}: ${response.statusText}`,
				) as Error & {
					response?: {
						status: number;
						statusText: string;
						data: unknown;
						headers: Record<string, string>;
					};
					config?: typeof config;
				};
				error.response = {
					status: response.status,
					statusText: response.statusText,
					data,
					headers: Object.fromEntries(response.headers.entries()),
				};
				error.config = config;
				throw error;
			}

			return {
				data,
				status: response.status,
				statusText: response.statusText,
				headers: Object.fromEntries(response.headers.entries()),
				config,
				request: {},
			};
		}

		// Use native HTTP for JSON/regular requests. Query params must be appended
		// here — the custom adapter bypasses axios's built-in param serialization.
		const response = await CapacitorHttp.request({
			url: appendParams(
				`${config.baseURL || ""}${config.url || ""}`,
				config.params,
			),
			method: config.method?.toUpperCase() || "GET",
			headers: config.headers as Record<string, string>,
			data: config.data,
		});

		// Check for HTTP errors (status >= 400)
		// CapacitorHttp doesn't throw on error status codes, so we must check manually
		if (response.status >= 400) {
			const error = new Error(`HTTP ${response.status}: Error`) as Error & {
				response?: {
					status: number;
					statusText: string;
					data: unknown;
					headers: Record<string, string>;
				};
				config?: typeof config;
			};
			error.response = {
				status: response.status,
				statusText: `${response.status}`,
				data: response.data,
				headers: response.headers,
			};
			error.config = config;
			throw error;
		}

		return {
			data: response.data,
			status: response.status,
			statusText: `${response.status}`,
			headers: response.headers,
			config,
			request: {},
		};
	} catch (error) {
		console.error("❌ [CAPACITOR] Native HTTP request failed:", {
			url: config.url,
			method: config.method,
			error,
		});
		throw error;
	}
};
